import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const card = { background: 'white', border: '1px solid #d8e4dd', borderRadius: '8px', padding: '16px', boxShadow: '0 12px 30px rgba(24,47,38,0.08)' };
const muted = { color: '#61726b', fontSize: '13px' };

function euro(value) {
  return Number(value || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function Pill({ tone = 'slate', children }) {
  const tones = {
    green: ['#dff3e9', '#064734', '#a7d9c2'],
    amber: ['#fff7d6', '#8a5a00', '#f4d36b'],
    red: ['#fff1ef', '#9f1d12', '#ffc9c2'],
    blue: ['#e8f0ff', '#23417a', '#bfd0ff'],
    slate: ['#eef4f1', '#334155', '#d8e4dd'],
  };
  const [bg, color, border] = tones[tone] || tones.slate;
  return <span style={{ background: bg, color, border: `1px solid ${border}`, borderRadius: '999px', padding: '4px 9px', fontSize: '11px', fontWeight: 900 }}>{children}</span>;
}

function Stat({ title, value, hint, tone = 'slate' }) {
  const colors = { green: '#0a6f4f', amber: '#a16207', red: '#b42318', blue: '#1d4ed8', slate: '#12231d' };
  return (
    <div style={card}>
      <div style={{ color: '#61726b', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</div>
      <div style={{ color: colors[tone], fontSize: '25px', fontWeight: 950, marginTop: '7px' }}>{value}</div>
      {hint && <div style={{ ...muted, marginTop: '4px' }}>{hint}</div>}
    </div>
  );
}

export default function ClosingPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);
  useEffect(() => { if (month) load(month); }, [month]);

  async function load(selected) {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/abschluss/monthly', { params: selected ? { monat: selected } : {} });
      setData(r.data);
      if (!month) setMonth(r.data.monat);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Monatsabschluss konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }

  async function saveSnapshot() {
    if (!data) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const r = await api.post(`/abschluss/monthly/${data.monat}/snapshot`);
      setMessage(`Monatsabschluss-Snapshot ${r.data.id} gespeichert.`);
      await load(data.monat);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Snapshot konnte nicht gespeichert werden');
    } finally {
      setBusy(false);
    }
  }

  if (loading && !data) return <div style={card}>Monatsabschluss wird geladen...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#064734', fontSize: '25px' }}>Monatsabschluss</h1>
          <p style={{ ...muted, margin: '4px 0 0' }}>Ein geführter Check für Bericht, Regeln, Warnungen und Sparpotenzial.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <select value={month} onChange={e => setMonth(e.target.value)} style={input}>
            {data?.months?.map(m => <option key={m.monat} value={m.monat}>{m.monat} ({m.anzahl})</option>)}
          </select>
          <button onClick={saveSnapshot} disabled={busy} style={button('#0a6f4f')}>{busy ? 'Speichere...' : 'Abschluss speichern'}</button>
        </div>
      </div>

      {error && <Notice tone="red">{error}</Notice>}
      {message && <Notice tone="green">{message}</Notice>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
            <Stat title="Offene Punkte" value={data.totals.offene_punkte} hint="aus der Abschlussliste" tone={data.totals.offene_punkte ? 'amber' : 'green'} />
            <Stat title="Ausgaben" value={euro(data.report.summary.ausgaben)} hint={data.monat} tone="blue" />
            <Stat title="Sparpotenzial" value={euro(data.totals.monatliches_potenzial)} hint={`${euro(data.totals.jahrespotenzial)} pro Jahr`} tone="green" />
            <Stat title="Snapshots" value={data.snapshots.length} hint="für diesen Monat" tone={data.snapshots.length ? 'green' : 'amber'} />
          </div>

          <section style={{ ...card, marginBottom: '12px' }}>
            <div style={sectionHead}>
              <h2 style={h2}>Abschluss-Checkliste</h2>
              <Pill tone={data.totals.offene_punkte ? 'amber' : 'green'}>{data.totals.offene_punkte ? 'offen' : 'fertig'}</Pill>
            </div>
            <div style={{ display: 'grid', gap: '8px' }}>
              {data.checklist.map(item => (
                <div key={item.key} style={row}>
                  <div>
                    <div style={{ color: '#12231d', fontWeight: 900 }}>{item.label}</div>
                    <div style={muted}>{item.detail}</div>
                  </div>
                  <Pill tone={item.done ? 'green' : 'amber'}>{item.done ? 'ok' : 'prüfen'}</Pill>
                </div>
              ))}
            </div>
          </section>

          {data.budgetStatus && (
            <section style={{ ...card, marginBottom: '12px', borderColor: data.budgetStatus.totals.red ? '#ffc9c2' : '#d8e4dd', background: data.budgetStatus.totals.red ? '#fffaf9' : 'white' }}>
              <div style={sectionHead}>
                <h2 style={{ ...h2, color: data.budgetStatus.totals.red ? '#9f1d12' : '#064734' }}>Budget-Ziele</h2>
                <Pill tone={data.budgetStatus.totals.red ? 'red' : data.budgetStatus.totals.amber ? 'amber' : 'green'}>{data.budgetStatus.totals.red || 0} über Limit</Pill>
              </div>
              {data.budgetStatus.activeItems.length ? data.budgetStatus.activeItems.slice(0, 8).map(item => (
                <div key={item.id} style={row}>
                  <div>
                    <div style={{ color: item.tone === 'red' ? '#9f1d12' : '#12231d', fontWeight: 950 }}>{item.kategorie}</div>
                    <div style={muted}>{euro(item.ausgaben)} von {euro(item.monatliches_limit)} · {item.status}</div>
                  </div>
                  <div style={{ color: item.tone === 'red' ? '#b42318' : item.tone === 'amber' ? '#a16207' : '#0a6f4f', fontWeight: 950 }}>{item.prozent}%</div>
                </div>
              )) : <Empty text="Noch keine Budget-Ziele gesetzt." />}
            </section>
          )}

          {data.anomalyRadar && (
            <section style={{ ...card, marginBottom: '12px', borderColor: data.anomalyRadar.totals.hoch ? '#ffc9c2' : '#d8e4dd' }}>
              <div style={sectionHead}>
                <h2 style={{ ...h2, color: data.anomalyRadar.totals.hoch ? '#9f1d12' : '#064734' }}>Anomalie-Radar</h2>
                <Pill tone={data.anomalyRadar.totals.hoch ? 'red' : data.anomalyRadar.totals.gesamt ? 'amber' : 'green'}>{data.anomalyRadar.totals.hoch || 0} hoch</Pill>
              </div>
              {(data.anomalyRadar.anomalies || []).slice(0, 6).map((item, idx) => (
                <button key={`${item.typ}-${idx}`} onClick={() => nav(item.detail_url)} style={{ ...row, width: '100%', background: 'white', border: '1px solid #eef4f1', borderRadius: '8px', cursor: 'pointer', textAlign: 'left', marginBottom: '8px' }}>
                  <div>
                    <div style={{ color: item.prioritaet === 'hoch' ? '#9f1d12' : '#12231d', fontWeight: 950 }}>{item.titel}</div>
                    <div style={muted}>{item.typ} · {item.text}</div>
                  </div>
                  <div style={{ color: item.prioritaet === 'hoch' ? '#b42318' : '#a16207', fontWeight: 950 }}>{euro(item.betrag)}</div>
                </button>
              ))}
            </section>
          )}

          {data.contractRadar && (
            <section style={{ ...card, marginBottom: '12px', borderColor: data.contractRadar.totals.kuendigen ? '#ffc9c2' : '#d8e4dd' }}>
              <div style={sectionHead}>
                <h2 style={{ ...h2, color: data.contractRadar.totals.kuendigen ? '#9f1d12' : '#064734' }}>Abo- und Vertragsradar</h2>
                <Pill tone={data.contractRadar.totals.kuendigen ? 'red' : data.contractRadar.totals.pruefen ? 'amber' : 'green'}>{data.contractRadar.totals.pruefen || 0} prüfen</Pill>
              </div>
              {data.contractRadar.items.length ? data.contractRadar.items.slice(0, 8).map(item => (
                <button key={`${item.empfaenger}-${item.kategorie}`} onClick={() => nav(item.detail_url)} style={{ ...row, width: '100%', background: 'white', border: '1px solid #eef4f1', borderRadius: '8px', cursor: 'pointer', textAlign: 'left', marginBottom: '8px' }}>
                  <div>
                    <div style={{ color: item.status === 'kuendigen' ? '#9f1d12' : '#12231d', fontWeight: 950 }}>{item.empfaenger}</div>
                    <div style={muted}>{item.kategorie} · nächst {item.naechste_erwartung} · {item.status}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#0a6f4f', fontWeight: 950 }}>{euro(item.jahreswert)}</div>
                    <div style={muted}>{euro(item.durchschnitt)} mtl.</div>
                  </div>
                </button>
              )) : <Empty text="Keine wiederkehrenden Verträge erkannt." />}
            </section>
          )}

          {data.geldfresserPlan && (
            <section style={{ ...card, marginBottom: '12px', borderColor: '#ffc9c2', background: '#fffaf9' }}>
              <div style={sectionHead}>
                <h2 style={{ ...h2, color: '#9f1d12' }}>Bestätigte Geldfresser</h2>
                <Pill tone={data.geldfresserPlan.items.length ? 'red' : 'green'}>{euro(data.geldfresserPlan.totals.jahrespotenzial)} / Jahr</Pill>
              </div>
              {data.geldfresserPlan.items.length ? data.geldfresserPlan.items.slice(0, 8).map(item => (
                <button key={`${item.empfaenger}-${item.kategorie}`} onClick={() => nav(item.detail_url)} style={{ ...row, width: '100%', background: 'white', border: '1px solid #ffe1dd', borderRadius: '8px', cursor: 'pointer', textAlign: 'left', marginBottom: '8px' }}>
                  <div>
                    <div style={{ color: '#9f1d12', fontWeight: 950 }}>{item.empfaenger}</div>
                    <div style={muted}>{item.kategorie} · {item.aktion} · {item.flags.join(', ')}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#b42318', fontWeight: 950 }}>{euro(item.jahrespotenzial)}</div>
                    <div style={muted}>{euro(item.monatliches_potenzial)} mtl.</div>
                  </div>
                </button>
              )) : <Empty text="Keine bestätigten Geldfresser für diesen Zeitraum." />}
            </section>
          )}

          <section style={{ ...card, marginBottom: '12px' }}>
            <div style={sectionHead}>
              <h2 style={h2}>Sparpotenzial und Kündigungskandidaten</h2>
              <Pill tone="green">{euro(data.totals.jahrespotenzial)} / Jahr</Pill>
            </div>
            {data.savings.length ? data.savings.map(item => (
              <div key={`${item.empfaenger}-${item.kategorie}`} style={row}>
                <div>
                  <div style={{ color: '#12231d', fontWeight: 950 }}>{item.empfaenger}</div>
                  <div style={muted}>{item.kategorie} · {item.anzahl} Buchungen · {item.empfehlung}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#0a6f4f', fontWeight: 950 }}>{euro(item.jahrespotenzial)}</div>
                  <div style={muted}>{euro(item.monatliches_potenzial)} mtl.</div>
                </div>
              </div>
            )) : <Empty text="Keine Sparpotenzial-Kandidaten gefunden." />}
          </section>

          <section style={{ ...card, marginBottom: '12px' }}>
            <div style={sectionHead}>
              <h2 style={h2}>Teurer als im Vormonat</h2>
              <Pill tone={data.changes.length ? 'amber' : 'green'}>{data.changes.length}</Pill>
            </div>
            {data.changes.length ? data.changes.map(item => (
              <div key={`${item.empfaenger}-${item.kategorie}`} style={row}>
                <div>
                  <div style={{ color: '#12231d', fontWeight: 900 }}>{item.empfaenger}</div>
                  <div style={muted}>{item.kategorie} · vorher {euro(item.vorher)}</div>
                </div>
                <div style={{ color: '#b45309', fontWeight: 950 }}>+{euro(item.differenz)}</div>
              </div>
            )) : <Empty text="Keine deutlichen Mehrkosten gegenüber dem Vormonat." />}
          </section>
        </>
      )}
    </div>
  );
}

function Notice({ tone, children }) {
  const red = tone === 'red';
  return <div style={{ background: red ? '#fff1ef' : '#dff3e9', color: red ? '#9f1d12' : '#064734', border: `1px solid ${red ? '#ffc9c2' : '#a7d9c2'}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontWeight: 800 }}>{children}</div>;
}

function Empty({ text }) {
  return <div style={{ color: '#61726b', textAlign: 'center', padding: '18px 8px' }}>{text}</div>;
}

const h2 = { margin: 0, color: '#064734', fontSize: '17px' };
const sectionHead = { display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '12px' };
const row = { display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', borderBottom: '1px solid #eef4f1', padding: '10px 0' };
const input = { border: '1px solid #d8e4dd', borderRadius: '8px', padding: '10px 11px', background: 'white', color: '#12231d', fontWeight: 800 };
function button(bg) {
  return { background: bg, color: 'white', border: 'none', borderRadius: '8px', padding: '10px 13px', cursor: 'pointer', fontWeight: 900 };
}
